import { callAPI } from '../../../../services/api.service';

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface ClassroomListItem {
  id: string;
  name: string;
  teacherEmail: string;
  groupCode?: string;
  description?: string;
  studentCount: number;
  projectCount: number;
  bookCount: number;
  createdAt: any;
  updatedAt: any;
}

export interface ClassroomProject {
  id: string;
  name: string;
  status?: string;
  isActive?: boolean;
  isDefaultProject?: boolean;
  startDate?: any;
  startDateTime?: string;
  projectTemplateId?: string;
  templateName?: string | null;
  studentCount: number;
  bookCount: number;
  parentResponseCount: number;
  orderedStudentCount: number;
  createdAt: any;
}

export interface ClassroomDetail extends ClassroomListItem {
  projects: ClassroomProject[];
}

export interface ProjectDetail extends ClassroomProject {
  // Only the dedicated project-detail endpoint includes finishedStudentCount
  // (it scans every book doc for the project — too expensive for the list view).
  finishedStudentCount: number;
  classroomId: string;
  classroomName: string | null;
  teacherEmail: string | null;
}

export interface ProjectBookItem {
  id: string;
  bookId: string;
  title: string;
  studentName: string;
  email: string;
  coverURL: string;
  assignmentStatus: string | null;
  step: number | null;
  isDraft: boolean;
  archived: boolean;
  timestamp: any;
  // Purchase status (the admin books endpoint returns full book docs, so these are present).
  purchasedCounter?: number | null;
  shippedCounter?: number | null;
  assignedByTeacher?: string;
  classroomId?: string;
  projectId?: string;
}

// ── Bulk "mark as purchased" (prepaid schools) ──────────────────────────────
export interface BulkPurchaseResult {
  bookId: string;
  success: boolean;
  skipped?: boolean;
  error?: string;
}

export interface ClassroomBookId {
  bookId: string;
  purchasedCounterValue: number;
}

// Server caps a batch at 25; the client streams larger selections in chunks.
export const BULK_PURCHASE_BATCH_SIZE = 25;

export interface TemplateOption {
  id: string;
  name: string;
  wpProjectId: string;
}

// ── API calls ───────────────────────────────────────────────────────────────

/**
 * Fetch paginated classrooms with optional teacher/classroom filters.
 */
export const getAdminClassrooms = async (
  page: number = 1,
  filters?: {
    teacherEmail?: string;
    classroomId?: string;
    search?: string;
    sortBy?: 'name' | 'created';
    sortDir?: 'asc' | 'desc';
  },
): Promise<{ classrooms: ClassroomListItem[]; totalPages: number; totalRecords: number; pageSize: number }> => {
  try {
    const params = new URLSearchParams({ pageNumber: String(page) });
    if (filters?.teacherEmail) params.set('teacherEmail', filters.teacherEmail);
    if (filters?.classroomId) params.set('classroomId', filters.classroomId);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.sortBy) params.set('sortBy', filters.sortBy);
    if (filters?.sortDir) params.set('sortDir', filters.sortDir);

    const response = await callAPI(`admin/classroom?${params.toString()}`, null, 'GET');
    return {
      classrooms: response?.classrooms || [],
      totalPages: response?.totalPages || 0,
      totalRecords: response?.totalRecords || 0,
      pageSize: response?.pageSize || 25,
    };
  } catch (error) {
    console.error('Failed to fetch admin classrooms:', error);
    return { classrooms: [], totalPages: 0, totalRecords: 0, pageSize: 25 };
  }
};

/**
 * Fetch a single classroom with project details.
 */
export const getAdminClassroomDetail = async (
  classroomId: string,
): Promise<ClassroomDetail | null> => {
  try {
    const response = await callAPI(`admin/classroom/${classroomId}`, null, 'GET');
    return response?.classroom || null;
  } catch (error) {
    console.error('Failed to fetch classroom detail:', error);
    return null;
  }
};

/**
 * Update classroom fields (name).
 */
export const updateAdminClassroom = async (
  classroomId: string,
  data: { name?: string },
  callback?: () => void,
): Promise<void> => {
  try {
    await callAPI(`admin/classroom/${classroomId}`, data, 'PUT');
    callback?.();
  } catch (error) {
    console.error('Failed to update classroom:', error);
  }
};

/**
 * Update a project within a classroom (start date, linked template).
 */
export const updateAdminProject = async (
  projectId: string,
  data: { startDate?: string; startDateTime?: string; projectTemplateId?: string; isDefaultProject?: boolean },
  callback?: () => void,
): Promise<void> => {
  try {
    await callAPI(`admin/classroom/projects/${projectId}`, data, 'PUT');
    callback?.();
  } catch (error) {
    console.error('Failed to update project:', error);
  }
};

/**
 * Fetch a single project with full counters (admin drill-down view).
 */
export const getAdminProjectDetail = async (
  projectId: string,
): Promise<ProjectDetail | null> => {
  try {
    const response = await callAPI(`admin/classroom/projects/${projectId}/details`, null, 'GET');
    return response?.project || null;
  } catch (error) {
    console.error('Failed to fetch project detail:', error);
    return null;
  }
};

/**
 * Fetch paginated books in a project (admin drill-down view).
 */
export const getAdminProjectBooks = async (
  projectId: string,
  page: number = 1,
  filters?: { search?: string; includeArchived?: boolean },
): Promise<{
  books: ProjectBookItem[];
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  truncated: boolean;
}> => {
  try {
    const params = new URLSearchParams({ pageNumber: String(page) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.includeArchived) params.set('includeArchived', 'true');

    const response = await callAPI(
      `admin/classroom/projects/${projectId}/books?${params.toString()}`,
      null,
      'GET',
    );
    return {
      books: response?.books || [],
      totalPages: response?.totalPages || 0,
      totalRecords: response?.totalRecords || 0,
      pageSize: response?.pageSize || 25,
      truncated: !!response?.truncated,
    };
  } catch (error) {
    console.error('Failed to fetch project books:', error);
    return { books: [], totalPages: 0, totalRecords: 0, pageSize: 25, truncated: false };
  }
};

export type PreOrderStatus = 'pending' | 'confirmed' | 'cancelled';

export interface ProjectPreOrder {
  id: string;
  studentUsername: string;
  firstName: string;
  lastName: string;
  email: string;
  orderDecision: 'yes' | 'no';
  status: PreOrderStatus;
  hasPayment: boolean;
  sessionId: string | null;
  /** Stripe refs for manual refunds — absent on pre-orders confirmed before the capture existed. */
  stripePaymentIntentId?: string | null;
  stripeInvoiceId?: string | null;
  preOrderBookCount: number;
  timestamp?: any;
}

export interface ProjectPreOrdersSummary {
  total: number;
  confirmed: number;
  pending: number;
  cancelled: number;
}

/**
 * Fetch parent pre-orders for a project with payment status (admin drill-down).
 */
export const getAdminProjectPreOrders = async (
  projectId: string,
): Promise<{ preOrders: ProjectPreOrder[]; summary: ProjectPreOrdersSummary }> => {
  const empty = { preOrders: [], summary: { total: 0, confirmed: 0, pending: 0, cancelled: 0 } };
  try {
    const response = await callAPI(`admin/classroom/projects/${projectId}/preorders`, null, 'GET');
    return {
      preOrders: response?.preOrders || [],
      summary: response?.summary || empty.summary,
    };
  } catch (error) {
    console.error('Failed to fetch project pre-orders:', error);
    return empty;
  }
};

/**
 * Fetch every book id (+ purchase status) in a classroom, for the bulk
 * "mark as purchased" select-all flow.
 */
export const fetchClassroomBookIds = async (
  classroomId: string,
  projectId?: string,
): Promise<ClassroomBookId[]> => {
  try {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    const response = await callAPI(`admin/classroom/${classroomId}/book-ids${qs}`, null, 'GET');
    return Array.isArray(response?.bookIds) ? response.bookIds : [];
  } catch (error) {
    console.error('Failed to fetch classroom book ids:', error);
    return [];
  }
};

/**
 * Bulk-mark a batch of books as purchased ($0 prepaid) for a classroom. The server
 * caps each call at BULK_PURCHASE_BATCH_SIZE — callers should chunk larger selections.
 * Always resolves to a per-book result array (synthesises failures on a bad response).
 */
export const bulkMarkBooksPurchased = async (
  classroomId: string,
  bookIds: string[],
  quantity = 1,
  reference?: string,
): Promise<BulkPurchaseResult[]> => {
  try {
    const response = await callAPI(
      `admin/classroom/${classroomId}/bulk-mark-purchased`,
      { bookIds, quantity, reference },
      'POST',
    );
    if (Array.isArray(response?.results)) return response.results;
    const errMsg = response?.error || 'Bulk purchase endpoint returned no results';
    return bookIds.map((bookId) => ({ bookId, success: false, error: errMsg }));
  } catch (error: any) {
    return bookIds.map((bookId) => ({ bookId, success: false, error: error?.message || 'Network error' }));
  }
};

/**
 * Fetch project templates for the dropdown selector.
 */
export const getTemplateOptions = async (): Promise<TemplateOption[]> => {
  try {
    const response = await callAPI('admin/classroom/templates/list', null, 'GET');
    return response?.templates || [];
  } catch (error) {
    console.error('Failed to fetch template options:', error);
    return [];
  }
};
